<template>
  <div ref="containerRef" class="pivot_table_container w-full h-full flex flex-col p-4">
    <SimpleTable
      :columns="tableColumns"
      :data="tableData"
      :scroll="tableScrollConfig"
      :table-props="{ columnDrag: false, rowClassName: getRowClassName, xVirtual: false, maxHeight: tableMaxHeight, rowHeight: 50, summaryFixed: showTotals && hasTotals ? 'bottom' : false }"
      :auto-height="false"
      identifier-key="key"
      class="pivot_table"
    >
      <template #headerCell="{ column }">
        <a-tooltip placement="top" color="var(--tooltip-bg)" :title="column.title">
          <span class="header_title">{{ column.title }}</span>
        </a-tooltip>
      </template>

      <template #bodyCell="{ column, record }">
        <div
          class="body_cell"
          :class="{
            row_cell: column.isRowHeader,
            value_cell: column.isValueHeader,
            total_row: record.isTotal,
          }"
        >
          <a-tooltip v-if="column.isValueHeader" placement="top" color="var(--tooltip-bg)" :title="formatValue(record[column.dataIndex], column)">
            <span class="value_text cell_content">
              {{ formatValue(record[column.dataIndex], column) }}
            </span>
          </a-tooltip>
          <a-tooltip v-else placement="top" color="var(--tooltip-bg)" :title="formatRowValue(record[column.dataIndex], column.dataIndex)">
            <span class="dimension_text cell_content">
              {{ formatRowValue(record[column.dataIndex], column.dataIndex) }}
            </span>
          </a-tooltip>
        </div>
      </template>
      <template v-if="showTotals && hasTotals" #summary>
        <s-table-summary-row>
          <s-table-summary-cell
            v-for="(column, colIndex) in flatLeafColumns"
            :key="column.key"
            :index="colIndex"
            :col-span="1"
          >
            <a-tooltip placement="top" color="var(--tooltip-bg)" :title="getTotalValue(column, colIndex)">
              <div
                class="total_cell cell_content"
                :class="{ total_value: column.isValueHeader }"
              >
                {{ getTotalValue(column, colIndex) }}
              </div>
            </a-tooltip>
          </s-table-summary-cell>
        </s-table-summary-row>
      </template>
    </SimpleTable>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from "vue";
import { STableSummaryRow, STableSummaryCell} from "@surely-vue/table";
import "@surely-vue/table/dist/index.css";
import SimpleTable from "@/src/components/custom_table_components/SimpleTable.vue";
import { formatLocaleNumber } from "@/src/utils/analyticsChartUtils.js";
import { formatVisualizationDateTimeValue, normalizeToISO, detectDatesPeriodType, formatDateByPeriod } from "@/src/utils/dateTimeUtils.js";
import store from "@/src/store/index.js";



const props = defineProps({
  chartData: { type: Object, default: () => ({}) },
});

const containerRef = ref(null);
const windowWidth = ref(window.innerWidth);
const containerHeight = ref(0);

const updateDimensions = () => {
  windowWidth.value = window.innerWidth;
  if (containerRef.value) {
    containerHeight.value = containerRef.value.clientHeight;
  }
};

let resizeObserver = null;

onMounted(() => {
  updateDimensions();
  if (containerRef.value) {
    resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(containerRef.value);
  }
});

onUnmounted(() => {
  if (resizeObserver) {
    resizeObserver.disconnect();
  }
});

const rows = computed(() => props.chartData?.rows || []);
const columns = computed(() => props.chartData?.columns || []);
const values = computed(() => props.chartData?.values || []);
const data = computed(() => props.chartData?.data || []);
const totals = computed(() => props.chartData?.totals || {});
const showTotals = computed(() => props.chartData?.show_totals !== false);
const hasTotals = computed(() => Object.keys(totals.value).length > 0);
const columnHierarchy = computed(() => props.chartData?.column_hierarchy || {});
const rawColumnOrder = computed(() => props.chartData?.column_order || []);

const columnOrder = computed(() => rawColumnOrder.value);

const renderedConfig = computed(() => store.getters["analyticsConfiguration/renderedChartConfiguration"]);

const getColumnSortConfig = (depth) => {
  const cols = renderedConfig.value?.columns || [];
  if (depth >= cols.length) return null;
  const sort = cols[depth]?.sort;
  if (!sort || sort === "none") return null;
  if (sort === "asc" || sort === "desc") return { type: "label", direction: sort };
  const match = sort.match(/^value_(asc|desc)__(.+)$/);
  if (match) {
    const valueId = match[2].includes("::") ? match[2].split("::").pop() : match[2];
    return { type: "value", direction: match[1], valueId };
  }
  return null;
};

const sortChildKeys = (keys, sortConfig, parentPath) => {
  if (!sortConfig) return keys;
  const sorted = [...keys];
  if (sortConfig.type === "label") {
    sorted.sort((a, b) => {
      const cmp = a.localeCompare(b, undefined, { sensitivity: "base" });
      return sortConfig.direction === "desc" ? -cmp : cmp;
    });
    return sorted;
  }
  if (sortConfig.type === "value") {
    const totalData = totals.value;
    const valueSuffix = `__${sortConfig.valueId}`;
    const colTotals = {};
    for (const colName of sorted) {
      const prefix = parentPath.length > 0 ? `${parentPath.join("__")}__${colName}__` : `${colName}__`;
      const directKey = parentPath.length > 0 ? `${parentPath.join("__")}__${colName}${valueSuffix}` : `${colName}${valueSuffix}`;
      colTotals[colName] = 0;
      for (const [k, v] of Object.entries(totalData)) {
        if (k === directKey || (k.startsWith(prefix) && k.endsWith(valueSuffix))) {
          colTotals[colName] += typeof v === "number" ? v : 0;
        }
      }
    }
    sorted.sort((a, b) => {
      return sortConfig.direction === "desc"
        ? colTotals[b] - colTotals[a]
        : colTotals[a] - colTotals[b];
    });
    return sorted;
  }
  return sorted;
};

const AGGREGATION_TO_PERIOD = {
  hourly: "hourly",
  daily: "daily",
  day: "daily",
  weekly: "weekly",
  monthly: "monthly",
  quarterly: "quarterly",
  yearly: "yearly",
};

const rowPeriodTypes = computed(() => {
  const types = {};
  rows.value.forEach((row) => {
    const colKey = row.id;
    if (row.aggregation && AGGREGATION_TO_PERIOD[row.aggregation]) {
      types[colKey] = AGGREGATION_TO_PERIOD[row.aggregation];
      return;
    }
    const dates = data.value
      .map((d) => d[colKey])
      .filter((v) => typeof v === "string")
      .map((v) => normalizeToISO(v))
      .filter(Boolean)
      .map((iso) => new Date(iso))
      .filter((d) => !isNaN(d.getTime()));
    types[colKey] = detectDatesPeriodType(dates);
  });
  return types;
});

const formatRowValue = (value, colKey) => {
  const periodType = rowPeriodTypes.value[colKey] || "unknown";
  if (periodType === "unknown") return formatVisualizationDateTimeValue(value);
  return formatDateByPeriod(value, periodType);
};

const buildHierarchicalColumns = (
  hierarchy,
  columnDefs,
  valueDefs,
  path = [],
  depth = 0,
  orderedKeys = [],
) => {
  const result = [];

  if (!hierarchy || typeof hierarchy !== "object" || Array.isArray(hierarchy)) {
    return result;
  }

  if (depth >= columnDefs.length) {
    valueDefs.forEach((value) => {
      if (!value || typeof value !== "object") return;
      const dataIndexPath = [...path, value.id].join("__");
      result.push({
        title: value.name || value.label || "Value",
        dataIndex: dataIndexPath,
        key: dataIndexPath,
        width: 100,
        isValueHeader: true,
        valueConfig: value,
        align: "right",
        ellipsis: false,
      });
    });
    return result;
  }

  const currentColumnDef = columnDefs[depth];
  if (!currentColumnDef) return result;

  let keysToIterate;
  if (orderedKeys.length > 0 && depth === 0) {
    keysToIterate = orderedKeys;
  } else {
    keysToIterate = Object.keys(hierarchy);
    const sortConfig = getColumnSortConfig(depth);
    if (sortConfig) {
      keysToIterate = sortChildKeys(keysToIterate, sortConfig, path);
    }
  }

  keysToIterate.forEach((key) => {
    if (!hierarchy[key]) return;

    const childHierarchy = hierarchy[key];
    if (childHierarchy === null || typeof childHierarchy !== "object") {
      return;
    }

    const newPath = [...path, key];

    const children = buildHierarchicalColumns(
      childHierarchy,
      columnDefs,
      valueDefs,
      newPath,
      depth + 1,
      [],
    );

    if (children.length > 0) {
      result.push({
        title: formatVisualizationDateTimeValue(key),
        key: newPath.join("__"),
        isGroupHeader: true,
        columnId: currentColumnDef.id,
        children: children,
      });
    }
  });

  return result;
};

const isValidArray = (arr) => Array.isArray(arr) && arr.length > 0;
const isValidObject = (obj) =>
  obj && typeof obj === "object" && !Array.isArray(obj);

const tableColumns = computed(() => {
  const cols = [];

  if (isValidArray(rows.value)) {
    rows.value.forEach((row, index) => {
      if (!isValidObject(row)) return;
      cols.push({
        title: row.name || row.label || `Row ${index + 1}`,
        dataIndex: row.id || `row_${index}`,
        key: row.id || `row_${index}`,
        width: 150,
        fixed: index === 0 ? "left" : false,
        isRowHeader: true,
        ellipsis: false,
      });
    });
  }

  const hasColumns =
    isValidArray(columns.value) && columns.value.every(isValidObject);
  const hasValues =
    isValidArray(values.value) && values.value.every(isValidObject);
  const hasHierarchy =
    isValidObject(columnHierarchy.value) &&
    Object.keys(columnHierarchy.value).length > 0;

  if (hasColumns && hasValues && hasHierarchy) {
    const hierarchicalCols = buildHierarchicalColumns(
      columnHierarchy.value,
      columns.value,
      values.value,
      [],
      0,
      columnOrder.value,
    );
    cols.push(...hierarchicalCols);

    if (showTotals.value) {
      const totalChildren = values.value.filter(isValidObject).map((value) => ({
        title: value.name || value.label || "Value",
        dataIndex: `total__${value.id}`,
        key: `total__${value.id}`,
        width: 100,
        isValueHeader: true,
        isTotal: true,
        valueConfig: value,
        align: "right",
        ellipsis: false,
        fixed: "right",
      }));

      if (totalChildren.length > 0) {
        cols.push({
          title: "Total",
          key: "total_group",
          isGroupHeader: true,
          fixed: "right",
          children: totalChildren,
        });
      }
    }
  } else if (hasValues) {
    values.value.forEach((value, index) => {
      if (!isValidObject(value)) return;
      cols.push({
        title: value.name || value.label || `Value ${index + 1}`,
        dataIndex: value.id || `value_${index}`,
        key: value.id || `value_${index}`,
        width: 120,
        isValueHeader: true,
        valueConfig: value,
        align: "right",
        ellipsis: false,
      });
    });
  }

  return cols;
});

const flattenColumns = (cols) => {
  const result = [];
  cols.forEach((col) => {
    if (col.children && col.children.length > 0) {
      result.push(...flattenColumns(col.children));
    } else {
      result.push(col);
    }
  });
  return result;
};

const flatLeafColumns = computed(() => {
  return flattenColumns(tableColumns.value);
});

const tableData = computed(() => {
  if (!isValidArray(data.value)) return [];

  return data.value.filter(isValidObject).map((row, index) => ({
    ...row,
    key: row.key || `row_${index}`,
  }));
});

const tableMaxHeight = computed(() => {
  const available = containerHeight.value - 32;
  return available > 0 ? available : 500;
});

const tableScrollConfig = computed(() => {
  const leafCount = flatLeafColumns.value.length;
  const estimatedWidth = leafCount * 120;

  return {
    x: estimatedWidth > windowWidth.value ? `${estimatedWidth}px` : "100%",
  };
});

const formatValue = (value, column) => {
  try {
    if (value === null || value === undefined) return "—";

    const config = column.valueConfig || {};
    const prefix = config.prefix || "";
    const suffix = config.suffix || "";

    if (typeof value === "number") {
      const formatted = formatLocaleNumber(value, {
        decimalPlaces: config.decimal_places ?? 0,
      });
      return `${prefix}${formatted}${suffix}`;
    }

    return `${prefix}${value}${suffix}`;
  } catch (error) {
    console.error("An error occurred while formatting value: ", error);
  }
};

const getTotalValue = (column, colIndex) => {
  try {
    if (column.isRowHeader) {
      return colIndex === 0 ? "Total" : "";
    }

    if (column.isValueHeader) {
      const totalKey = column.dataIndex;
      const totalValue = totals.value[totalKey];
      if (totalValue !== undefined) {
        return formatValue(totalValue, column);
      }
    }

    return "—";
  } catch (error) {
    console.error("An error occurred while getting total value: ", error);
  }
};

const getRowClassName = (record) => {
  try {
    if (record.isTotal) return "total_row";
    if (record.isSubtotal) return "subtotal_row";
    return "";
  } catch (error) {
    console.error("An error occurred while getting row class name: ", error);
  }
};
</script>

<style scoped>
.pivot_table_container {
  background: var(--background-color);
}

.header_title,
.cell_content {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.pivot_table :deep(.surely-table-header-cell) {
  background: var(--table-header-bg) !important;
  color: var(--secondary-color);
  border-bottom: 1px solid var(--white-opacity-20);
  border-right: 1px solid var(--white-opacity-20);
  @apply font-semibold text-sm px-3 py-1;
  text-align: center;
}

.pivot_table :deep(.surely-table-header-cell:last-child) {
  border-right: none;
}

.pivot_table :deep(.surely-table) {
  --surely-table-header-cell-split-color: transparent;
}

.pivot_table :deep(.surely-table-cell-group-title) {
  border-bottom: 1px solid var(--white-opacity-20);
}

.pivot_table :deep(.surely-table-body-cell) {
  border-bottom: 1px solid var(--white-opacity-8);
  border-right: 1px solid var(--white-opacity-20);
  color: var(--foreground-rgb);
  background: var(--background-color);
  @apply text-sm px-3 py-1;
}

.pivot_table :deep(.surely-table-body-cell:last-child) {
  border-right: none;
}


.pivot_table :deep(.surely-table-row) {
  background: transparent;
  @apply transition-colors duration-150;
}

.pivot_table :deep(.surely-table-row:hover .surely-table-body-cell) {
  background: linear-gradient(var(--white-opacity-4), var(--white-opacity-4)), var(--background-color) !important;
}

.body_cell.row_cell {
  font-weight: 500;
}

.body_cell.value_cell {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.body_cell.total_row {
  font-weight: 600;
  background: var(--white-opacity-4);
}

.total_cell {
  font-weight: 600;
}

.total_cell.total_value {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.pivot_table :deep(.surely-table-horizontal-scroll-viewport),
.pivot_table :deep(.surely-table-body-horizontal-scroll-container) {
  scrollbar-width: thin;
  scrollbar-color: var(--white-opacity-12) transparent;
}

.pivot_table :deep(.surely-table-horizontal-scroll-viewport)::-webkit-scrollbar,
.pivot_table :deep(.surely-table-body-horizontal-scroll-container)::-webkit-scrollbar {
  height: 8px;
}

.pivot_table :deep(.surely-table-horizontal-scroll-viewport)::-webkit-scrollbar-track,
.pivot_table :deep(.surely-table-body-horizontal-scroll-container)::-webkit-scrollbar-track {
  background: transparent;
}

.pivot_table :deep(.surely-table-horizontal-scroll-viewport)::-webkit-scrollbar-thumb,
.pivot_table :deep(.surely-table-body-horizontal-scroll-container)::-webkit-scrollbar-thumb {
  background: var(--white-opacity-12);
  border-radius: 4px;
}

.pivot_table :deep(.surely-table-horizontal-scroll-viewport)::-webkit-scrollbar-thumb:hover,
.pivot_table :deep(.surely-table-body-horizontal-scroll-container)::-webkit-scrollbar-thumb:hover {
  background: var(--white-opacity-20);
}
</style>
